(() => {
  'use strict';

  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function randomNumber() {
    if (window.crypto && window.crypto.getRandomValues) {
      const buffer = new Uint32Array(1);
      window.crypto.getRandomValues(buffer);
      return buffer[0] / 4294967296;
    }
    return Math.random();
  }

  function shuffle(items) {
    const copy = items.slice();
    for (let index = copy.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(randomNumber() * (index + 1));
      [copy[index], copy[swapIndex]] = [copy[swapIndex], copy[index]];
    }
    return copy;
  }

  function initHeader() {
    const header = document.querySelector('[data-site-header]');
    const toggle = document.querySelector('[data-menu-toggle]');
    const nav = document.querySelector('[data-nav]');
    if (!header) return;

    const syncHeader = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    syncHeader();
    if (header.dataset.foScrollBound !== 'true') {
      header.dataset.foScrollBound = 'true';
      window.addEventListener('scroll', syncHeader, { passive: true });
    }

    if (!toggle || !nav || toggle.dataset.foBound === 'true') return;
    toggle.dataset.foBound = 'true';

    const setOpen = (open) => {
      nav.classList.toggle('is-open', open);
      toggle.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? 'Close navigation' : 'Open navigation');
      document.body.classList.toggle('menu-open', open && window.innerWidth <= 900);
    };

    toggle.addEventListener('click', () => setOpen(!nav.classList.contains('is-open')));
    nav.addEventListener('click', (event) => {
      if (event.target.closest('a')) setOpen(false);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') setOpen(false);
    });
    document.addEventListener('click', (event) => {
      if (nav.classList.contains('is-open') && !header.contains(event.target)) setOpen(false);
    });
    window.addEventListener('resize', () => {
      if (window.innerWidth > 900) setOpen(false);
    }, { passive: true });
  }

  function parseVariantOptions(option) {
    try {
      return JSON.parse(option.dataset.options || '[]');
    } catch (_) {
      return [];
    }
  }

  function initProductForms(root = document) {
    root.querySelectorAll('[data-product-form]').forEach((form) => {
      if (form.dataset.foBound === 'true') return;
      form.dataset.foBound = 'true';

      const select = form.querySelector('[data-variant-select]');
      const optionInputs = Array.from(form.querySelectorAll('[data-option-input]'));
      const price = form.closest('.product-info')?.querySelector('[data-product-price]');
      const compare = form.closest('.product-info')?.querySelector('[data-product-compare]');
      const addButton = form.querySelector('[data-add-to-cart]');
      const quantityInput = form.querySelector('[data-quantity-input]');
      const minusButton = form.querySelector('[data-quantity-minus]');
      const plusButton = form.querySelector('[data-quantity-plus]');

      const syncVariantUI = () => {
        if (!select) return;
        const option = select.options[select.selectedIndex];
        if (!option) return;
        const available = option.dataset.available === 'true';

        if (price && option.dataset.price) price.textContent = option.dataset.price;
        if (compare) {
          const comparePrice = option.dataset.compare || '';
          compare.textContent = comparePrice;
          compare.hidden = !comparePrice || comparePrice === option.dataset.price;
        }
        if (addButton) {
          addButton.disabled = !available;
          addButton.textContent = available
            ? (addButton.dataset.availableLabel || 'Add to cart')
            : (addButton.dataset.soldOutLabel || 'Sold out');
        }

        const url = new URL(window.location.href);
        url.searchParams.set('variant', option.value);
        window.history.replaceState({}, '', url.toString());
      };

      const syncFromButtons = () => {
        if (!select || optionInputs.length === 0) return;

        const selectedValues = [];
        optionInputs.forEach((input) => {
          if (input.checked) selectedValues[Number(input.dataset.optionPosition) - 1] = input.value;
        });

        const matchingOption = Array.from(select.options).find((variantOption) => {
          const values = parseVariantOptions(variantOption);
          return values.length === selectedValues.length && values.every((value, index) => value === selectedValues[index]);
        });

        if (matchingOption) {
          select.value = matchingOption.value;
          form.classList.remove('has-invalid-variant');
        } else {
          form.classList.add('has-invalid-variant');
          if (addButton) {
            addButton.disabled = true;
            addButton.textContent = 'Combination unavailable';
          }
          return;
        }

        syncVariantUI();
      };

      optionInputs.forEach((input) => {
        input.addEventListener('change', () => {
          const label = Array.from(form.querySelectorAll('label[for]')).find((item) => item.htmlFor === input.id);
          if (label && !reducedMotion.matches) {
            label.classList.remove('is-just-selected');
            void label.offsetWidth;
            label.classList.add('is-just-selected');
            window.setTimeout(() => label.classList.remove('is-just-selected'), 320);
          }
          syncFromButtons();
        });
      });
      if (select) select.addEventListener('change', syncVariantUI);

      const setQuantity = (nextValue) => {
        if (!quantityInput) return;
        const min = Number(quantityInput.min || 1);
        const max = quantityInput.max ? Number(quantityInput.max) : Infinity;
        const safeValue = Math.min(max, Math.max(min, Number.isFinite(nextValue) ? nextValue : min));
        quantityInput.value = String(safeValue);
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      };

      minusButton?.addEventListener('click', () => setQuantity(Number(quantityInput?.value || 1) - 1));
      plusButton?.addEventListener('click', () => setQuantity(Number(quantityInput?.value || 1) + 1));
      quantityInput?.addEventListener('change', () => setQuantity(Number(quantityInput.value)));

      if (select) {
        if (optionInputs.length) syncFromButtons();
        else syncVariantUI();
      }
    });
  }

  function initButtonRipples(root = document) {
    if (reducedMotion.matches) return;

    root.querySelectorAll('.btn, .pill, .flavor-button, .variant-option-button, .collection-switcher a').forEach((button) => {
      if (button.dataset.foRippleBound === 'true') return;
      button.dataset.foRippleBound = 'true';

      button.addEventListener('pointerdown', (event) => {
        if (button.matches(':disabled, [aria-disabled="true"]')) return;
        const rect = button.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height) * 0.55;
        const ripple = document.createElement('span');
        ripple.className = 'fo-ripple';
        ripple.style.width = `${size}px`;
        ripple.style.height = `${size}px`;
        ripple.style.left = `${event.clientX - rect.left}px`;
        ripple.style.top = `${event.clientY - rect.top}px`;
        button.appendChild(ripple);
        ripple.addEventListener('animationend', () => ripple.remove(), { once: true });
      }, { passive: true });
    });
  }

  function findUniqueCategoryAssignment(cards) {
    const ordered = cards
      .filter((card) => card.candidates.length > 0)
      .map((card) => ({ ...card, candidates: shuffle(card.candidates) }))
      .sort((a, b) => a.candidates.length - b.candidates.length);

    const search = (index, usedProducts, usedImages, picks) => {
      if (index >= ordered.length) return picks;
      const current = ordered[index];

      for (const candidate of current.candidates) {
        if (usedProducts.has(candidate.productId) || usedImages.has(candidate.src)) continue;

        const nextProducts = new Set(usedProducts);
        const nextImages = new Set(usedImages);
        nextProducts.add(candidate.productId);
        nextImages.add(candidate.src);

        const nextPicks = new Map(picks);
        nextPicks.set(current.card, candidate);
        const result = search(index + 1, nextProducts, nextImages, nextPicks);
        if (result) return result;
      }

      return null;
    };

    return search(0, new Set(), new Set(), new Map());
  }

  function initRandomCategoryImages(root = document) {
    root.querySelectorAll('[data-random-category-grid]').forEach((grid) => {
      if (grid.dataset.foRandomized === 'true') return;
      grid.dataset.foRandomized = 'true';

      const cards = Array.from(grid.querySelectorAll('[data-random-category-card]')).map((card) => {
        const image = card.querySelector('[data-random-category-image]');
        const candidates = Array.from(card.querySelectorAll('[data-category-image-candidate]'))
          .map((node) => ({
            productId: node.dataset.productId || '',
            src: node.dataset.src || '',
            srcset: node.dataset.srcset || ''
          }))
          .filter((candidate) => candidate.productId && candidate.src);
        return { card, image, candidates };
      });

      let assignment = findUniqueCategoryAssignment(cards);

      if (!assignment) {
        assignment = new Map();
        const usedProducts = new Set();
        const usedImages = new Set();
        cards.forEach((item) => {
          const candidates = shuffle(item.candidates);
          const choice = candidates.find((candidate) => !usedProducts.has(candidate.productId) && !usedImages.has(candidate.src))
            || candidates.find((candidate) => !usedImages.has(candidate.src))
            || candidates[0];
          if (!choice) return;
          assignment.set(item.card, choice);
          usedProducts.add(choice.productId);
          usedImages.add(choice.src);
        });
      }

      cards.forEach((item) => {
        const choice = assignment.get(item.card);
        if (!item.image || !choice) return;

        const markReady = () => item.image.classList.add('is-image-ready');
        item.image.addEventListener('load', markReady, { once: true });
        item.image.sizes = '(max-width: 560px) 84vw, (max-width: 900px) 100vw, 33vw';
        if (choice.srcset) item.image.srcset = choice.srcset;
        item.image.src = choice.src;
        if (item.image.complete) markReady();
      });
    });
  }

  function initRevealAnimations(root = document) {
    const targets = root.querySelectorAll([
      '.section-header',
      '.hero-discover',
      '.featured-products-grid .product-card',
      '.drink-category-grid .category-card',
      '.newsletter',
      '.collection-head',
      '.collection-switcher',
      '.collection-product-grid .product-card',
      '.main-product .product-gallery',
      '.main-product .product-info',
      '.page-head',
      '.search-form',
      '.empty-state',
      '.site-footer .footer-grid'
    ].join(','));

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach((element) => element.classList.add('fo-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('fo-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -7% 0px', threshold: 0.08 });

    targets.forEach((element, index) => {
      if (element.dataset.foRevealBound === 'true') return;
      element.dataset.foRevealBound = 'true';
      element.classList.add('fo-reveal');
      element.dataset.foDelay = String(index % 4);
      observer.observe(element);
    });
  }


  function initRoundTwoHero(root = document) {
    if (reducedMotion.matches || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    root.querySelectorAll('[data-hero-parallax]').forEach((media) => {
      if (media.dataset.foParallaxBound === 'true') return;
      media.dataset.foParallaxBound = 'true';
      let frame = 0;
      let mx = 0;
      let my = 0;
      media.addEventListener('pointermove', (event) => {
        const rect = media.getBoundingClientRect();
        mx = ((event.clientX - rect.left) / rect.width - .5) * -9;
        my = ((event.clientY - rect.top) / rect.height - .5) * -7;
        if (frame) return;
        frame = requestAnimationFrame(() => {
          media.style.setProperty('--fo-hero-shift-x', `${mx.toFixed(2)}px`);
          media.style.setProperty('--fo-hero-shift-y', `${my.toFixed(2)}px`);
          frame = 0;
        });
      }, { passive: true });
      media.addEventListener('pointerleave', () => {
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
        media.style.setProperty('--fo-hero-shift-x', '0px');
        media.style.setProperty('--fo-hero-shift-y', '0px');
      });
    });
  }

  function init(root = document) {
    initHeader();
    initProductForms(root);
    initRandomCategoryImages(root);
    initButtonRipples(root);
    initRevealAnimations(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
